import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import API_BASE_URL from '../config';
import styles from '../css-isolated/consult_symptoms.module.css';
import authFetch from '../utils/authFetch';

function ConsultSymptoms({ hideSystemHeader = false, formData: externalFormData, setFormData: setExternalFormData, isDarkMode = true }) {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [formData, setFormData] = useState(externalFormData || {
    date: '',
    chiefComplaint: '',
    diagnosis: ''
  });
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const submitTimerRef = useRef(null);

  // Date Modal State
  const [isDateModalOpen, setIsDateModalOpen] = useState(false);
  const [modalMonth, setModalMonth] = useState('');
  const [modalDay, setModalDay] = useState('');
  const [modalYear, setModalYear] = useState('');
  const [modalTextDate, setModalTextDate] = useState('');
  const [modalError, setModalError] = useState('');

  // Cleanup submit timer on unmount
  useEffect(() => {
    return () => {
      if (submitTimerRef.current) clearTimeout(submitTimerRef.current);
    };
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.profile-section')) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);

  // Sync external form data changes to local state
  useEffect(() => {
    if (externalFormData) {
      setFormData(externalFormData);
    }
  }, [externalFormData]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const newData = {
      ...formData,
      [name]: value
    };
    setFormData(newData);
    // Sync with external state if provided
    if (setExternalFormData) {
      setExternalFormData(newData);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.date || !formData.chiefComplaint) {
      alert('Please fill in all required fields: Date and Chief Complaint');
      return;
    }

    try {
      // API call to save consultation symptoms
      const response = await authFetch(`${API_BASE_URL}/api/consultation-symptoms`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      await response.json();
      setSubmitSuccess(true);
      
      // Reset form after successful submission
      submitTimerRef.current = setTimeout(() => {
        setFormData({
          date: '',
          chiefComplaint: '',
          diagnosis: ''
        });
        setSubmitSuccess(false);
      }, 2000);
      
    } catch (error) {
      console.error('Error saving consultation symptoms:', error);
      alert('Error saving consultation symptoms. Please try again.');
    }
  };

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const handleLogout = () => {
    // Add logout logic here
  };

  // Helper function for date validation
  const getDaysInMonth = (month, year) => {
    if (!month || !year) return 31;
    const monthNum = parseInt(month);
    if (monthNum === 2) {
      const yearNum = parseInt(year);
      const isLeapYear = (yearNum % 4 === 0 && yearNum % 100 !== 0) || (yearNum % 400 === 0);
      return isLeapYear ? 29 : 28;
    }
    if ([4, 6, 9, 11].includes(monthNum)) return 30;
    return 31;
  };

  // Format date to MM/DD/YYYY for display
  const formatDateForDisplay = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
  };

  const openDatePicker = () => {
    setIsDateModalOpen(true);
  };

  const handleTodayClick = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    const formattedDate = `${year}-${month}-${day}`;
    
    const newData = {
      ...formData,
      date: formattedDate
    };
    setFormData(newData);
    if (setExternalFormData) {
      setExternalFormData(newData);
    }
  };

  // Render date picker modal using Portal
  const renderDatePickerModal = () => {
    if (!isDateModalOpen) return null;

    return ReactDOM.createPortal(
      <div className={`${styles.modalOverlay} ${!isDarkMode ? styles.lightMode : ''}`} onClick={() => {
        setIsDateModalOpen(false);
        setModalError('');
      }}>
        <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
          <div className={styles.modalHeader}>
            <h3>📅 Select Consultation Date</h3>
            <button 
              type="button"
              aria-label="Close date modal"
              className={styles.modalCloseBtn}
              onClick={() => {
                setIsDateModalOpen(false);
                setModalError('');
              }}
            >
              &times;
            </button>
          </div>
          
          <div className={styles.modalBody}>
            <div className={styles.modalRow}>
              <div className={styles.modalField}>
                <label>MONTH</label>
                <select 
                  value={modalMonth}
                  onChange={(e) => {
                    setModalMonth(e.target.value);
                    setModalError('');
                    if (e.target.value && modalDay && modalYear) {
                      const m = e.target.value.padStart(2, '0');
                      const d = modalDay.padStart(2, '0');
                      setModalTextDate(`${m}/${d}/${modalYear}`);
                    }
                    if (modalDay && modalYear) {
                      const maxDays = getDaysInMonth(e.target.value, modalYear);
                      if (parseInt(modalDay) > maxDays) {
                        setModalDay(String(maxDays));
                        const m = e.target.value.padStart(2, '0');
                        setModalTextDate(`${m}/${String(maxDays).padStart(2, '0')}/${modalYear}`);
                      }
                    }
                  }}
                >
                  <option value="">Month</option>
                  <option value="1">January</option>
                  <option value="2">February</option>
                  <option value="3">March</option>
                  <option value="4">April</option>
                  <option value="5">May</option>
                  <option value="6">June</option>
                  <option value="7">July</option>
                  <option value="8">August</option>
                  <option value="9">September</option>
                  <option value="10">October</option>
                  <option value="11">November</option>
                  <option value="12">December</option>
                </select>
              </div>

              <div className={styles.modalField}>
                <label>DAY</label>
                <select 
                  value={modalDay}
                  onChange={(e) => {
                    setModalDay(e.target.value);
                    setModalError('');
                    if (modalMonth && e.target.value && modalYear) {
                      const m = modalMonth.padStart(2, '0');
                      const d = e.target.value.padStart(2, '0');
                      setModalTextDate(`${m}/${d}/${modalYear}`);
                    }
                  }}
                >
                  <option value="">Day</option>
                  {Array.from({ length: getDaysInMonth(modalMonth, modalYear) }, (_, i) => i + 1).map(day => (
                    <option key={day} value={day}>{day}</option>
                  ))}
                </select>
              </div>

              <div className={styles.modalField}>
                <label>YEAR</label>
                <select 
                  value={modalYear}
                  onChange={(e) => {
                    setModalYear(e.target.value);
                    setModalError('');
                    if (modalMonth && modalDay && e.target.value) {
                      const m = modalMonth.padStart(2, '0');
                      const d = modalDay.padStart(2, '0');
                      setModalTextDate(`${m}/${d}/${e.target.value}`);
                    }
                    if (modalMonth && modalDay) {
                      const maxDays = getDaysInMonth(modalMonth, e.target.value);
                      if (parseInt(modalDay) > maxDays) {
                        setModalDay(String(maxDays));
                        const m = modalMonth.padStart(2, '0');
                        setModalTextDate(`${m}/${String(maxDays).padStart(2, '0')}/${e.target.value}`);
                      }
                    }
                  }}
                >
                  <option value="">Year</option>
                  {Array.from({ length: new Date().getFullYear() - 1900 + 1 }, (_, i) => new Date().getFullYear() - i).map(year => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className={styles.modalRow}>
              <div className={styles.modalField} style={{ flex: 1 }}>
                <label>TYPE DATE (MM/DD/YYYY)</label>
                <input
                  type="text"
                  placeholder="mm/dd/yyyy"
                  value={modalTextDate}
                  onChange={(e) => {
                    let val = e.target.value;
                    if (val.length < modalTextDate.length) {
                      setModalTextDate(val);
                      setModalError('');
                      return;
                    }
                    val = val.replace(/\D/g, '');
                    if (val.length >= 2) {
                      val = val.slice(0, 2) + '/' + val.slice(2);
                    }
                    if (val.length >= 5) {
                      val = val.slice(0, 5) + '/' + val.slice(5);
                    }
                    if (val.length > 10) {
                      val = val.slice(0, 10);
                    }
                    setModalTextDate(val);
                    setModalError('');
                    const parts = val.split('/');
                    if (parts[0] && parts[0].length === 2) {
                      const m = parseInt(parts[0]);
                      if (m >= 1 && m <= 12) {
                        setModalMonth(String(m));
                      }
                    }
                    if (parts[1] && parts[1].length === 2) {
                      const d = parseInt(parts[1]);
                      const maxDays = getDaysInMonth(parts[0], parts[2]);
                      if (d >= 1 && d <= maxDays) {
                        setModalDay(String(d));
                      }
                    }
                    if (parts[2] && parts[2].length === 4) {
                      const y = parseInt(parts[2]);
                      const currentYear = new Date().getFullYear();
                      if (y <= currentYear) {
                        setModalYear(String(y));
                      }
                    }
                  }}
                  maxLength={10}
                />
              </div>
            </div>

            {modalError && (
              <div className={styles.modalError}>
                ⚠️ {modalError}
              </div>
            )}

            <div className={styles.modalHelper}>
              ℹ️ Select from dropdowns or type the date directly
            </div>
          </div>

          <div className={styles.modalFooter}>
            <button 
              className={styles.modalBtnGhost}
              onClick={() => {
                setIsDateModalOpen(false);
                setModalMonth('');
                setModalDay('');
                setModalYear('');
                setModalTextDate('');
                setModalError('');
              }}
            >
              Cancel
            </button>
            <button 
              className={styles.modalBtn}
              onClick={() => {
                if (!modalMonth || !modalDay || !modalYear) {
                  setModalError('Please select month, day, and year');
                  return;
                }
                const m = parseInt(modalMonth);
                const d = parseInt(modalDay);
                const y = parseInt(modalYear);
                if (m < 1 || m > 12) {
                  setModalError('Invalid month');
                  return;
                }
                const maxDays = getDaysInMonth(modalMonth, modalYear);
                if (d < 1 || d > maxDays) {
                  setModalError(`Invalid day for selected month (max ${maxDays})`);
                  return;
                }
                const currentYear = new Date().getFullYear();
                if (y > currentYear) {
                  setModalError(`Year cannot be greater than ${currentYear}`);
                  return;
                }
                const formattedMonth = modalMonth.padStart(2, '0');
                const formattedDay = modalDay.padStart(2, '0');
                const isoDate = `${modalYear}-${formattedMonth}-${formattedDay}`;
                const newData = {
                  ...formData,
                  date: isoDate
                };
                setFormData(newData);
                if (setExternalFormData) {
                  setExternalFormData(newData);
                }
                setIsDateModalOpen(false);
                setModalError('');
              }}
            >
              Apply
            </button>
          </div>
        </div>
      </div>,
      document.body
    );
  };

  // Render embedded version when hideSystemHeader is true
  if (hideSystemHeader) {
    return (
      <div className={`${styles.embeddedContainer} ${!isDarkMode ? styles.lightMode : ''}`}>
        {/* Form Section - Direct content without separate header */}
        <div className={`${styles.formSection} ${styles.consultationFormSection}`}>
          <div className={styles.sectionHeader}>
            <div className={styles.sectionIcon}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M9 11H3m3 8H3m5-13H3m13 13h-2a4 4 0 01-4-4V6a4 4 0 014-4h2v14z" stroke="currentColor" strokeWidth="2"/>
                <path d="M9 7h1a4 4 0 014 4v1a4 4 0 004 4h1" stroke="currentColor" strokeWidth="2"/>
              </svg>
            </div>
            <h2 className={styles.sectionTitle}>Consultation Symptoms</h2>
          </div>
          
          <form onSubmit={handleSubmit} className={styles.consultationForm}>
            {/* Date field */}
            <div className={styles.formGridSingle}>
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Date *</label>
                <div className={styles.dateInputWrapper}>
                  <input 
                    type="text"
                    name="date" 
                    value={formData.date ? formatDateForDisplay(formData.date) : ''} 
                    onClick={openDatePicker}
                    readOnly
                    className={`${styles.formInput} ${styles.datePickerInput}`}
                    placeholder="Select date..."
                    required
                  />
                  <button 
                    type="button"
                    onClick={handleTodayClick}
                    className={styles.todayButton}
                  >
                    📅 Today
                  </button>
                </div>
              </div>
            </div>

            {/* Row 2: Chief Complaint and Diagnosis/Assessment */}
            <div className={styles.formGrid}>
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Chief Complaint (Symptoms Based) *</label>
                <textarea 
                  name="chiefComplaint" 
                  value={formData.chiefComplaint} 
                  onChange={handleInputChange} 
                  className={styles.formTextarea} 
                  placeholder="Describe the main symptoms and complaints..."
                  rows="4"
                  required
                />
              </div>
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Diagnosis/Assessment</label>
                <textarea 
                  name="diagnosis" 
                  value={formData.diagnosis} 
                  onChange={handleInputChange} 
                  className={styles.formTextarea} 
                  placeholder="Enter diagnosis, assessment, or recommendations..."
                  rows="4"
                />
              </div>
            </div>

            {/* Action Buttons - Removed per user request */}
          </form>

          {/* Success Message */}
          {submitSuccess && (
            <div className={styles.successMessage}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" stroke="currentColor" strokeWidth="2"/>
              </svg>
              <p>Consultation symptoms saved successfully!</p>
            </div>
          )}
        </div>

        {/* Date Modal - Rendered via Portal */}
        {renderDatePickerModal()}
      </div>
    );
  }

  // Regular standalone version
  return (
    <div className={`${styles.consultSymptomsContainer} ${!isDarkMode ? styles.lightMode : ''}`}>
      {/* System Header */}
      <header className={styles.systemHeader}>
        <div className={styles.headerLeft}>
          <div className={styles.systemLogo}>
            <img src="/BJMPRO-CAR.png" alt="BJMP CAR Logo" className={styles.systemLogoImg} />
            <div className={styles.systemTitle}>
              <h1>MediCast: BJMP Health Monitoring System</h1>
              <p>Bureau of Jail Management and Penology</p>
            </div>
          </div>
        </div>
        
        <div className={styles.headerRight}>
          <div className={styles.currentDateTime}>
            <div className={styles.dateTime}>
              <span className={styles.date}>{new Date().toLocaleDateString()}</span>
              <span className={styles.time}>{new Date().toLocaleTimeString()}</span>
            </div>
          </div>
          
          <div className={`${styles.profileSection} profile-section`}>
            <div className={styles.profileInfo} onClick={toggleDropdown}>
              <div className={styles.profileAvatar}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" stroke="currentColor" strokeWidth="2"/>
                  <circle cx="12" cy="7" r="4" stroke="currentColor" strokeWidth="2"/>
                </svg>
              </div>
              <div className={styles.profileDetails}>
                <span className={styles.userName}>Administrator</span>
                <span className={styles.userRole}>System Admin</span>
              </div>
              <svg className={`${styles.dropdownArrow} ${isDropdownOpen ? styles.open : ''}`} width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <polyline points="6,9 12,15 18,9" stroke="currentColor" strokeWidth="2"/>
              </svg>
            </div>
            
            {isDropdownOpen && (
              <div className={styles.dropdownMenu}>
                <div className={styles.dropdownItem}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" stroke="currentColor" strokeWidth="2"/>
                    <circle cx="12" cy="7" r="4" stroke="currentColor" strokeWidth="2"/>
                  </svg>
                  Profile Settings
                </div>
                <div className={styles.dropdownDivider}></div>
                <div className={styles.dropdownItem} onClick={handleLogout}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" stroke="currentColor" strokeWidth="2"/>
                    <polyline points="16,17 21,12 16,7" stroke="currentColor" strokeWidth="2"/>
                    <line x1="21" y1="12" x2="9" y2="12" stroke="currentColor" strokeWidth="2"/>
                  </svg>
                  Logout
                </div>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className={styles.mainContent}>
        <div className={styles.contentWrapper}>
          {/* Content Header */}
          <div className={styles.contentHeader}>
            <div className={styles.headerContent}>
              <div className={styles.headerText}>
                <h1 className={styles.pageTitle}>Consultation Symptoms</h1>
                <p className={styles.pageSubtitle}>Record patient consultation and symptoms</p>
              </div>
            </div>
          </div>

          {/* Consultation Form Section */}
          <div className={styles.formContainer}>
            <div className={`${styles.formSection} ${styles.consultationFormSection}`}>
            <div className={styles.sectionHeader}>
              <div className={styles.sectionIcon}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9 11H3m3 8H3m5-13H3m13 13h-2a4 4 0 01-4-4V6a4 4 0 014-4h2v14z" stroke="currentColor" strokeWidth="2"/>
                  <path d="M9 7h1a4 4 0 014 4v1a4 4 0 004 4h1" stroke="currentColor" strokeWidth="2"/>
                </svg>
              </div>
              <h2 className={styles.sectionTitle}>Consultation Symptoms</h2>
            </div>
            
            <form onSubmit={handleSubmit} className={styles.consultationForm}>
              {/* Date field */}
              <div className={styles.formGridSingle}>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Date *</label>
                  <div className={styles.dateInputWrapper}>
                    <input 
                      type="text"
                      name="date" 
                      value={formData.date ? formatDateForDisplay(formData.date) : ''} 
                      onClick={openDatePicker}
                      readOnly
                      className={`${styles.formInput} ${styles.datePickerInput}`}
                      placeholder="Select date..."
                      required
                    />
                    <button 
                      type="button"
                      onClick={handleTodayClick}
                      className={styles.todayButton}
                    >
                      📅 Today
                    </button>
                  </div>
                </div>
              </div>

              {/* Row 2: Chief Complaint and Diagnosis/Assessment */}
              <div className={styles.formGrid}>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Chief Complaint (Symptoms Based) *</label>
                  <textarea 
                    name="chiefComplaint" 
                    value={formData.chiefComplaint} 
                    onChange={handleInputChange} 
                    className={styles.formTextarea} 
                    placeholder="Describe the main symptoms and complaints..."
                    rows="4"
                    required
                  />
                </div>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Diagnosis/Assessment</label>
                  <textarea 
                    name="diagnosis" 
                    value={formData.diagnosis} 
                    onChange={handleInputChange} 
                    className={styles.formTextarea} 
                    placeholder="Enter diagnosis, assessment, or recommendations..."
                    rows="4"
                  />
                </div>
              </div>
              
              {/* Form Buttons - Removed per user request */}
            </form>

            {/* Success Message */}
            {submitSuccess && (
              <div className={styles.successMessage}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" stroke="currentColor" strokeWidth="2"/>
                </svg>
                <p>Consultation symptoms saved successfully!</p>
              </div>
            )}
          </div>

          {/* Date Modal - Rendered via Portal */}
          {renderDatePickerModal()}
        </div>
        </div>
      </main>
    </div>
  );
}

export default ConsultSymptoms;